// EventShortList.tsx
import React, { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, type GridRenderCellParams, type GridRowIdGetter } from '@mui/x-data-grid';
import { api } from "../../../api/eventApi";
import { EventRecord } from "../../../types/event.ts";
import { useFetch } from "../../../hooks/useFetch2";
import { useTranslation } from 'react-i18next';
import { Plate } from '../../../types/plate.ts';
import i18n from '../../../i18n';
import LocalizedGrid from '../../localizedGrid/LocalizedGridCook3.tsx';
import { formatDateTime } from '../../../tools/util.ts';
import ActionButtons from '../../ActionButtons.tsx';
import { PhotoCamera } from '@mui/icons-material';
import { FormModal } from '../../modal/FormModal.tsx';
import EventForm from '../events/EventForm.tsx';

interface EventShortListProps {
    plate?: Plate | null;
}

export const EventShortList: React.FC<EventShortListProps> = ({ plate }) => {
    const { t } = useTranslation();
    const { data, setData, error, update, loading } = useFetch<EventRecord[]>(api.getFiltered, [], {
        registration_number: plate?.registration_number || '',
        limit: 20,
    });
    const [isExporting, setIsExporting] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<EventRecord | undefined>();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const columns: GridColDef[] = useMemo(() => {
        const baseColumns: GridColDef[] = [
            { field: "point_name", headerName: t('EventList.point_name'), flex: 2 },
            { field: "checkpoint_name", headerName: t('EventList.checkpoint_name'), flex: 2 },
            { field: "origin_name", headerName: t('EventList.origin_name'), flex: 2 },
            { field: "ts_start", headerName: t('EventList.ts_start'), flex: 3, valueFormatter: (v) => formatDateTime(v) },
            { field: "registration_number", headerName: t('EventList.registration_number'), flex: 2 },
            { field: "car_brand", headerName: t('EventList.car_brand'), flex: 2 },
            { field: "car_owner", headerName: t('EventList.car_owner'), flex: 2 },
            {
                field: 'actions',
                headerName: t('EventList.actions'),
                width: 60,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<EventRecord>) => (
                    <ActionButtons actions={[{ type: 'extended', action: () => handleOpenModal(params.row), icon: PhotoCamera }]} />
                ),
            },
        ];

        if (plate) {
            const hiddenFields = ["registration_number", "car_brand", "car_owner"];
            return baseColumns.filter(col => !hiddenFields.includes(col.field));
        }

        return baseColumns;
    }, [t, plate]);

    const handleOpenModal = (row: EventRecord) => {
        setSelectedEvent(row);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

    return (

    <Paper sx={{ p: 0, maxWidth: 1600, mx: "auto", mt: 0 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography variant="body1">
                {t('EventShortList.Events')}
            </Typography>
        </Box>

        <LocalizedGrid
            gridId='EventShortList'
            lang={i18n.language as any}
            rows={data}
            columns={columns} 
            getRowId={(row) => row.id}
            loading={loading}
            sx={{ 
                maxWidth: '1600px',
                width: '100%',
                height: '600px',
            }}
            hideFooter
            hideToolbar={true}
            initialState={{
                columns: {columnVisibilityModel: {ts_start: true}}
            }}
       />

        <Typography color='error'>{error?.message}</Typography>
      
        <FormModal open={isModalOpen} onClose={handleCloseModal}>
            <EventForm
                eventRecord={selectedEvent}
                onCancel={handleCloseModal}
            />
        </FormModal>
     </Paper>
    );
}